import { getSessionUUID } from '../components/lib/AuthToken';
import logger from '../utils/logger';


const invoiceRequest = async (path = '', options = {}) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch(`/invoices${path}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionToken}`,
                ...(options.headers || {}),
            },
        });
        const data = await response.json();
        return { status: response.status, data };
    } catch (error) {
        logger.error('Invoice request failed:', error);
        return { status: 500, data: { error: error.message || 'Invoice request failed' } };
    }
};

const queryString = (params) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) query.set(key, value);
    });
    const value = query.toString();
    return value ? `?${value}` : '';
};

export const fetchInvoiceCustomers = () => invoiceRequest('/customers');
export const fetchInvoiceAccountCodes = (includeArchived = false) =>
    invoiceRequest(`/account-codes${queryString({ include_archived: includeArchived })}`);
export const createInvoiceAccountCode = (payload) => invoiceRequest('/account-codes', {
    method: 'POST', body: JSON.stringify(payload),
});
export const updateInvoiceAccountCode = (accountCodeId, payload) =>
    invoiceRequest(`/account-codes/${accountCodeId}`, {
        method: 'PUT', body: JSON.stringify(payload),
    });
export const fetchInvoiceCoverage = (filters = {}) => invoiceRequest(`/coverage${queryString(filters)}`);
export const searchInvoices = (filters = {}) => invoiceRequest(queryString(filters));
export const fetchInvoice = (invoiceId) => invoiceRequest(`/${invoiceId}`);
export const createInvoiceDraft = (payload) => invoiceRequest('/drafts', {
    method: 'POST', body: JSON.stringify(payload),
});
export const uploadPallettrackInvoiceCsv = async (file, customerId, accountCodeId) =>
    invoiceRequest('/drafts/upload', {
        method: 'POST',
        body: JSON.stringify({
            csv_text: await file.text(),
            filename: file.name,
            customer_id: customerId,
            account_code_id: accountCodeId,
        }),
    });
export const downloadPallettrackInvoiceTemplate = () =>
    downloadInvoiceFile('/drafts/upload/template', 'pallettrack-invoice-upload-template.csv');
export const generateInvoiceDrafts = (serviceDate, normalAccountCodeId, networkAccountCodeId) => invoiceRequest('/drafts/generate', {
    method: 'POST',
    body: JSON.stringify({
        service_date: serviceDate,
        normal_account_code_id: normalAccountCodeId || null,
        network_account_code_id: networkAccountCodeId || null,
    }),
});
export const updateInvoiceDraft = (invoiceId, payload) => invoiceRequest(`/${invoiceId}`, {
    method: 'PUT', body: JSON.stringify(payload),
});
export const issueInvoice = (invoiceId) => invoiceRequest(`/${invoiceId}/issue`, { method: 'POST' });
export const markInvoicePaid = (invoiceId) => invoiceRequest(`/${invoiceId}/paid`, { method: 'POST' });
export const voidInvoice = (invoiceId) => invoiceRequest(`/${invoiceId}/void`, { method: 'POST' });

export const downloadInvoiceCsv = async (filters) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch(`/invoices/export${queryString(filters)}`, {
            headers: { 'Authorization': `Bearer ${sessionToken}` },
        });
        if (!response.ok) {
            const data = await response.json();
            return { status: response.status, data };
        }
        const blob = await response.blob();
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?([^";]+)"?/i);
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = match?.[1] || 'xero-invoices.csv';
        link.click();
        URL.revokeObjectURL(url);
        return { status: 200 };
    } catch (error) {
        logger.error('Invoice CSV request failed:', error);
        return { status: 500, data: { error: error.message || 'Unable to download invoice CSV' } };
    }
};

const downloadInvoiceFile = async (path, fallbackFilename) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch(`/invoices${path}`, {
            headers: { 'Authorization': `Bearer ${sessionToken}` },
        });
        if (!response.ok) {
            return { status: response.status, data: await response.json() };
        }
        const blob = await response.blob();
        const disposition = response.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?([^";]+)"?/i);
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = match?.[1] || fallbackFilename;
        link.click();
        URL.revokeObjectURL(url);
        return { status: 200 };
    } catch (error) {
        logger.error('Invoice file download failed:', error);
        return { status: 500, data: { error: error.message || 'Unable to download file' } };
    }
};

export const openInvoicePdf = async (invoiceId, download = false) => {
    try {
        const sessionToken = await getSessionUUID();
        const response = await fetch(`/invoices/${invoiceId}/pdf?download=${download}`, {
            headers: { 'Authorization': `Bearer ${sessionToken}` },
        });
        if (!response.ok) {
            const data = await response.json();
            return { status: response.status, data };
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        if (download) {
            const disposition = response.headers.get('content-disposition') || '';
            const match = disposition.match(/filename="?([^";]+)"?/i);
            const link = document.createElement('a');
            link.href = url;
            link.download = match?.[1] || `invoice-${invoiceId}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } else {
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
        return { status: 200 };
    } catch (error) {
        logger.error('Invoice PDF request failed:', error);
        return { status: 500, data: { error: error.message || 'Unable to open invoice PDF' } };
    }
};